'use client';

import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

import type { Manifest } from './manifest-fetcher';
import type { ManifestEntry } from './assets';

const ManifestContext = createContext<Manifest | null>(null);

/**
 * Per-route manifest slices published by the assets-uploader to the public R2
 * bucket (see `lips-healthcare-1` docs/architecture/route-asset-manifests.md).
 * Each page fetches ONLY its own tiny slice at runtime, so a replaced asset
 * reaches the live site on the next page load with no rebuild — without pulling
 * the full ~1 MB manifest.
 *
 * The uploader maintains `<base>/<route>.json` (and `<base>/_global.json`) with
 * `Cache-Control: public, max-age=60, stale-while-revalidate=600`.
 */
export const RUNTIME_SLICE_BASE_URL =
  'https://assets.lipshealthcare.com/manifest/routes';

/** Slice used by the root provider (site chrome) and by providers with no route. */
export const GLOBAL_SLICE_ROUTE = '_global';

// Module-level cache + shared in-flight promise per slice, so many concurrent
// ManifestProvider instances for the same route trigger exactly one request.
const sliceCache = new Map<string, Manifest>();
const inFlightRequests = new Map<string, Promise<Manifest | null>>();

function normalizeSliceKey(route: string | undefined): string {
  const cleaned = String(route ?? '')
    .replace(/^\/+|\/+$/g, '')
    .split('/')
    .filter(Boolean)
    .join('/');
  return cleaned || GLOBAL_SLICE_ROUTE;
}

export function routeSliceUrl(route: string | undefined): string {
  const key = normalizeSliceKey(route);
  const path = key
    .split('/')
    .map((segment) => encodeURIComponent(segment))
    .join('/');
  return `${RUNTIME_SLICE_BASE_URL}/${path}.json`;
}

function isManifestEntry(value: unknown): value is ManifestEntry {
  return (
    typeof value === 'object' &&
    value !== null &&
    typeof (value as { hashedName?: unknown }).hashedName === 'string'
  );
}

/**
 * A slice is a flat map of manifest key -> client projection. An empty object
 * is valid (a route with no route-scoped assets still has a global slice); any
 * malformed payload is rejected so the build-time fallback is used instead.
 */
export function isValidSlice(value: unknown): value is Manifest {
  if (typeof value !== 'object' || value === null || Array.isArray(value)) {
    return false;
  }
  return Object.values(value).every(isManifestEntry);
}

/** Test-only: drop module-level caches between cases. */
export function resetRouteSliceCache(): void {
  sliceCache.clear();
  inFlightRequests.clear();
}

export function fetchRouteSlice(
  route: string | undefined,
): Promise<Manifest | null> {
  const key = normalizeSliceKey(route);
  const cached = sliceCache.get(key);
  if (cached) return Promise.resolve(cached);
  const pending = inFlightRequests.get(key);
  if (pending) return pending;
  if (typeof fetch !== 'function') return Promise.resolve(null);

  // Wrapped in Promise.resolve().then so a missing/failing fetch rejects into
  // the catch below instead of throwing synchronously.
  const request = Promise.resolve()
    .then(() => fetch(routeSliceUrl(key), { cache: 'no-cache' }))
    .then((response) => (response.ok ? response.json() : null))
    .then((data) => {
      if (isValidSlice(data)) {
        sliceCache.set(key, data);
        return data;
      }
      return null;
    })
    .catch(() => null)
    .finally(() => {
      inFlightRequests.delete(key);
    });

  inFlightRequests.set(key, request);
  return request;
}

/**
 * Nested providers MERGE with the nearest parent instead of shadowing it:
 * `merged = { ...parent, ...manifest }` — child keys override parent keys.
 *
 * After mount the provider also fetches the fresh public R2 slice for its route
 * (once per page load, shared across every instance) and merges it LAST, so
 * runtime entries win over both the global and any nested build-time slices.
 * The build-time value remains the initial render and the fallback if the
 * fetch fails, the slice is missing, or the payload is invalid. Because the
 * fetch is retried on every full page load with `cache: 'no-cache'` (CDN
 * revalidates after max-age), stale assets are not shown indefinitely.
 */
export function ManifestProvider({
  manifest,
  route,
  children,
}: {
  manifest: Manifest;
  route?: string;
  children: ReactNode;
}) {
  const parent = useContext(ManifestContext);
  const sliceKey = normalizeSliceKey(route);
  const [remoteManifest, setRemoteManifest] = useState<Manifest | null>(
    () => sliceCache.get(sliceKey) ?? null,
  );

  useEffect(() => {
    let active = true;
    fetchRouteSlice(sliceKey).then((remote) => {
      if (active && remote) setRemoteManifest(remote);
    });
    return () => {
      active = false;
    };
  }, [sliceKey]);

  const value = useMemo(() => {
    const merged = parent ? { ...parent, ...manifest } : manifest;
    return remoteManifest ? { ...merged, ...remoteManifest } : merged;
  }, [parent, manifest, remoteManifest]);

  return (
    <ManifestContext.Provider value={value}>
      {children}
    </ManifestContext.Provider>
  );
}

export function useManifest(): Manifest {
  const ctx = useContext(ManifestContext);
  if (!ctx) {
    throw new Error(
      'useManifest must be used within a <ManifestProvider>. ' +
        'Wrap your root layout with <ManifestProvider manifest={getClientManifest()}>',
    );
  }
  return ctx;
}

export type { Manifest };
export type { ManifestEntry };
